import { lazy, Suspense } from "react";
import { BrandedPageSkeleton } from "@/components/common/BrandedPageSkeleton";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { PublicRoute } from "./components/auth/PublicRoute";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminGuard } from "./components/auth/AdminGuard";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CelebrationProvider } from "./hooks/useCelebration";
import { AuthProvider } from "./contexts/AuthContext";
import { GlobalCelebrationModal } from "./components/gamification/GlobalCelebrationModal";
import { PointsCelebrationProvider } from "./components/gamification/PointsCelebrationProvider";
import { GiftPopupProvider } from "./components/rewards/GiftPopupProvider";
import { RewardReadyPopup } from "./components/rewards/RewardReadyPopup";
import { DailyOfferPopup } from "./components/daily-offer/DailyOfferPopup";
import { LiveRegionProvider } from "./components/accessibility/LiveRegion";
import { SkipLink } from "./components/accessibility/SkipLink";
import { SectionErrorBoundary } from "./components/SectionErrorBoundary";
import { NetworkStatusMonitor } from "./components/common/NetworkStatusMonitor";
import { ScrollLockReset } from "./components/common/ScrollLockReset";
import { InstallPrompt } from "./components/pwa/InstallPrompt";
import { IOSInstallHint } from "./components/pwa/IOSInstallHint";
import { InstallMomentTrigger } from "./components/pwa/InstallMomentTrigger";
import { PwaInstallRewardMount } from "./components/pwa/PwaInstallRewardMount";


// Eager load critical routes
import Landing from "./pages/Landing";
import Index from "./pages/Index";

// Lazy load non-critical routes
const Connections = lazy(() => import("./pages/Connections").then(m => ({ default: m.Connections })));
const Assign = lazy(() => import("./pages/Assign").then(m => ({ default: m.Assign })));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const PublicTask = lazy(() => import("./pages/PublicTask"));
const ConnectionInvite = lazy(() => import("./pages/ConnectionInvite"));
const CommunityGoals = lazy(() => import("./pages/CommunityGoals").then(m => ({ default: m.CommunityGoals })));
const CommunityGoalPassport = lazy(() => import("./pages/CommunityGoalPassport"));
const ProfilePage = lazy(() => import("./pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const DesignPreview = lazy(() => import("./pages/DesignPreview"));
const MyProgressEnhanced = lazy(() => import("./pages/MyProgressEnhanced"));

const MyRewardsEnhanced = lazy(() => import("./pages/MyRewardsEnhanced"));
const RewardsHub = lazy(() => import("./pages/RewardsHub"));

const NewCertificate = lazy(() => import("./pages/NewCertificate"));
const MyCertificates = lazy(() => import("./pages/MyCertificates"));
const PublicCertificate = lazy(() => import("./pages/PublicCertificate"));
const Marketplace = lazy(() => import("./pages/Marketplace").then(m => ({ default: m.Marketplace })));


const WishlistPage = lazy(() => import("./pages/Wishlist"));
const GoalDetailPage = lazy(() => import("./pages/GoalDetailPage"));
const GoalsPage = lazy(() => import("./pages/GoalsPage"));
const PublicWishlistPage = lazy(() => import("./pages/PublicWishlist"));
const AdvertiserDashboard = lazy(() => import("./pages/AdvertiserDashboard").then(m => ({ default: m.AdvertiserDashboard })));
const AdvertiserProfile = lazy(() => import("./pages/AdvertiserProfile").then(m => ({ default: m.AdvertiserProfile })));
const AdvertiserGoalOffers = lazy(() => import("./pages/AdvertiserGoalOffers").then(m => ({ default: m.AdvertiserGoalOffers })));
const OffersQueuePage = lazy(() => import("./pages/admin/OffersQueuePage"));
const MatchesPage = lazy(() => import("./pages/MatchesPage").then(m => ({ default: m.MatchesPage })));
const AdvertisersPage = lazy(() => import("./pages/admin/AdvertisersPage").then(m => ({ default: m.AdvertisersPage })));
const AdvertiserOrderPage = lazy(() => import("./pages/admin/AdvertiserOrderPage").then(m => ({ default: m.AdvertiserOrderPage })));
const CommonGoalsPage = lazy(() => import("./pages/admin/CommonGoalsPage").then(m => ({ default: m.CommonGoalsPage })));


const PartnerAnalyticsPage = lazy(() => import("./pages/admin/PartnerAnalyticsPage").then(m => ({ default: m.PartnerAnalyticsPage })));
const CommonGoalAnalyticsPage = lazy(() => import("./pages/admin/CommonGoalAnalyticsPage"));
const UserInsightsPage = lazy(() => import("./pages/admin/UserInsightsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ArchivedGoals = lazy(() => import("./pages/ArchivedGoals"));
const CommunityGoalImpact = lazy(() => import("./pages/CommunityGoalImpact"));
const CityTasksPage = lazy(() => import("./pages/CityTasksPage"));
const CityTasksAdminPage = lazy(() => import("./pages/admin/CityTasksAdminPage"));
const FeedbackInboxPage = lazy(() => import("./pages/admin/FeedbackInboxPage"));
const PlacesAdminPage = lazy(() => import("./pages/admin/PlacesAdminPage"));
const CreatorJoin = lazy(() => import("./pages/CreatorJoin"));
const CreatorsDirectory = lazy(() => import("./pages/CreatorsDirectory"));
const CreatorsClaimPage = lazy(() => import("./pages/CreatorsClaimPage"));
const CreatorInvoiceTool = lazy(() => import("./pages/CreatorInvoiceTool"));
const CreatorProfile = lazy(() => import("./pages/CreatorProfile"));
const CreatorDashboard = lazy(() => import("./pages/CreatorDashboard"));
const CreatorsAdminPage = lazy(() => import("./pages/admin/CreatorsAdminPage"));

const PlaceSubmissionsAdminPage = lazy(() => import("./pages/admin/PlaceSubmissionsAdminPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const ListYourBusinessPage = lazy(() => import("./pages/ListYourBusinessPage"));
const PlaceBusinessPage = lazy(() => import("./pages/PlaceBusinessPage"));
const ExplorePage = lazy(() => import("./pages/ExplorePage"));

// Optimized QueryClient with better defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Accessible loading fallback using shared component
const PageLoader = () => (
  <BrandedPageSkeleton />
);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
          <LiveRegionProvider>
            <CelebrationProvider>
              <TooltipProvider>
                <SkipLink targetId="main-content" />
                <BrowserRouter>
                  <ScrollLockReset />
                  <AuthProvider>
                  <Suspense fallback={<PageLoader />}>
                  <Routes>
                    {/* Public Routes - redirect to dashboard if logged in */}
                    <Route path="/" element={
                      <PublicRoute>
                        <SectionErrorBoundary section="Landing">
                          <Landing />
                        </SectionErrorBoundary>
                      </PublicRoute>
                    } />
                    
                    {/* Public pages accessible without auth */}
                    <Route path="/certificate/:id" element={
                      <SectionErrorBoundary section="Public Certificate"><PublicCertificate /></SectionErrorBoundary>
                    } />
                    
                    <Route path="/invite/:userId" element={
                      <SectionErrorBoundary section="Connection Invite"><ConnectionInvite /></SectionErrorBoundary>
                    } />
                    <Route path="/t/:token" element={
                      <SectionErrorBoundary section="Public Task"><PublicTask /></SectionErrorBoundary>
                    } />
                    <Route path="/wishlist/:shareToken" element={
                      <SectionErrorBoundary section="Public Wishlist"><PublicWishlistPage /></SectionErrorBoundary>
                    } />
                    <Route path="/privacy-policy" element={
                      <SectionErrorBoundary section="Privacy Policy"><PrivacyPolicyPage /></SectionErrorBoundary>
                    } />
                    <Route path="/terms-of-service" element={
                      <SectionErrorBoundary section="Terms"><TermsOfServicePage /></SectionErrorBoundary>
                    } />
                    <Route path="/support" element={
                      <SectionErrorBoundary section="Support"><SupportPage /></SectionErrorBoundary>
                    } />
                    <Route path="/community-goals/:id/impact" element={
                      <SectionErrorBoundary section="Community Goal Impact"><CommunityGoalImpact /></SectionErrorBoundary>
                    } />
                    <Route path="/community-goals/:id" element={
                      <SectionErrorBoundary section="Community Goal Passport"><CommunityGoalPassport /></SectionErrorBoundary>
                    } />
                    <Route path="/auth" element={
                      <SectionErrorBoundary section="Auth"><AuthPage /></SectionErrorBoundary>
                    } />
                    <Route path="/explore" element={
                      <SectionErrorBoundary section="Explore Map"><ExplorePage /></SectionErrorBoundary>
                    } />
                    <Route path="/feedback" element={
                      <SectionErrorBoundary section="Feedback"><FeedbackPage /></SectionErrorBoundary>
                    } />
                    <Route path="/list-your-business" element={
                      <SectionErrorBoundary section="List Business"><ListYourBusinessPage /></SectionErrorBoundary>
                    } />
                    {/* Public per-place "For this business" page — read-only self-onboarding. */}
                    <Route path="/business/place/:mapPlaceId" element={
                      <SectionErrorBoundary section="Place Business"><PlaceBusinessPage /></SectionErrorBoundary>
                    } />
                    {/* Free public invoice generator — no sign-in required. */}
                    <Route path="/creators/invoice" element={
                      <SectionErrorBoundary section="Creator Invoice Tool"><CreatorInvoiceTool /></SectionErrorBoundary>
                    } />
                    <Route path="/creators/join" element={
                      <SectionErrorBoundary section="Creator Join"><CreatorJoin /></SectionErrorBoundary>
                    } />
                    {/* Public creator registry — verified creators only. */}
                    <Route path="/creators" element={
                      <SectionErrorBoundary section="Creator Directory"><CreatorsDirectory /></SectionErrorBoundary>
                    } />
                    {/* VIP invite claim — pre-seeded profiles attach in one tap. */}
                    <Route path="/creators/claim" element={
                      <SectionErrorBoundary section="Creator Claim"><CreatorsClaimPage /></SectionErrorBoundary>
                    } />
                    <Route path="/creators/:handle" element={
                      <SectionErrorBoundary section="Creator Profile"><CreatorProfile /></SectionErrorBoundary>
                    } />
                    {/* Merged contributor hub — creator + partner sections in one page. */}
                    <Route path="/me/contributor" element={
                      <SectionErrorBoundary section="Contributor Hub"><CreatorDashboard /></SectionErrorBoundary>
                    } />
                    {/* Legacy aliases kept so existing links and CTAs keep working. */}
                    <Route path="/creators/me" element={<Navigate to="/me/contributor" replace />} />
                    <Route path="/business" element={<Navigate to="/me/contributor" replace />} />

                    <Route path="/admin/offers-queue" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Offers Queue"><OffersQueuePage /></SectionErrorBoundary>
                      </AdminGuard>
                    } />


                    
                    {/* Protected Routes - require authentication */}
                    <Route path="/dashboard" element={
                      <SectionErrorBoundary section="Dashboard">
                        <Index />
                      </SectionErrorBoundary>
                    } />
                    <Route path="/goals" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Goals">
                          <GoalsPage />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/goals/:goalId" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Goal Detail">
                          <GoalDetailPage />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/archive" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Archive">
                          <ArchivedGoals />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/connections" element={<Navigate to="/assign?tab=people" replace />} />
                    <Route path="/assign" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Assign">
                          <Assign />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/assign/new" element={<Navigate to="/assign?new=1" replace />} />
                    <Route path="/common-goals" element={
                      <SectionErrorBoundary section="Community Goals">
                        <CommunityGoals />
                      </SectionErrorBoundary>
                    } />
                    {/* Legacy/long-form trail list URL used across the app and canonical tags */}
                    <Route path="/community-goals" element={
                      <SectionErrorBoundary section="Community Goals">
                        <CommunityGoals />
                      </SectionErrorBoundary>
                    } />
                    {/* 4-Pillar alias — "Passports" is the user-facing name for Community Goals */}
                    <Route path="/passports" element={
                      <SectionErrorBoundary section="Passports">
                        <CommunityGoals />
                      </SectionErrorBoundary>
                    } />
                    <Route path="/passports/:id" element={
                      <SectionErrorBoundary section="Passport Detail"><CommunityGoalPassport /></SectionErrorBoundary>
                    } />

                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Profile">
                          <ProfilePage />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/my-progress" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Progress">
                          <MyProgressEnhanced />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/my-rewards" element={<Navigate to="/rewards" replace />} />
                    <Route path="/rewards" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Rewards Hub">
                          <RewardsHub />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/certificate/new" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="New Certificate">
                          <NewCertificate />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/certificates" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Certificates">
                          <MyCertificates />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/my-certificates" element={<Navigate to="/certificates" replace />} />
                    <Route path="/marketplace" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Marketplace">
                          <Marketplace />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/rewards-marketplace" element={<Navigate to="/assign?tab=people" replace />} />
                    <Route path="/gifts" element={<Navigate to="/assign?tab=people" replace />} />
                    <Route path="/shop" element={<Navigate to="/rewards" replace />} />
                    <Route path="/wishlist" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Wishlist">
                          <WishlistPage />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/advertiser-dashboard" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Advertiser Dashboard">
                          <AdvertiserDashboard />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/advertiser-profile" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Advertiser Profile">
                          <AdvertiserProfile />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/advertiser-goal-offers" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Goal Offers">
                          <AdvertiserGoalOffers />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/matches" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="Matches">
                          <MatchesPage />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* Admin Routes - protected by AdminGuard */}
                    <Route path="/admin/advertisers" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Admin Advertisers">
                          <AdvertisersPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    <Route path="/admin/advertiser-orders/:advertiserId" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Advertiser Orders">
                          <AdvertiserOrderPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    <Route path="/admin/common-goals" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Admin Common Goals">
                          <CommonGoalsPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    {/* Merged into /admin/common-goals (Partner Offers tab) */}
                    <Route path="/admin/goal-offers" element={<Navigate to="/admin/common-goals?tab=offers" replace />} />
                    <Route path="/admin/partner-analytics" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Partner Analytics">
                          <PartnerAnalyticsPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    <Route path="/admin/common-goals/:goalId/analytics" element={
                      <SectionErrorBoundary section="Community Goal Analytics">
                        <CommonGoalAnalyticsPage />
                      </SectionErrorBoundary>
                    } />
                    <Route path="/admin/user-insights" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="User Insights">
                          <UserInsightsPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    <Route path="/admin/city-tasks" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Admin City Tasks">
                          <CityTasksAdminPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    <Route path="/admin/feedback" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Admin Feedback">
                          <FeedbackInboxPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    <Route path="/admin/places" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Admin Places">
                          <PlacesAdminPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    <Route path="/admin/creators" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Admin Creators">
                          <CreatorsAdminPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />

                    <Route path="/admin/place-submissions" element={
                      <AdminGuard>
                        <SectionErrorBoundary section="Place Submissions">
                          <PlaceSubmissionsAdminPage />
                        </SectionErrorBoundary>
                      </AdminGuard>
                    } />
                    <Route path="/city-tasks" element={
                      <ProtectedRoute>
                        <SectionErrorBoundary section="City Tasks">
                          <CityTasksPage />
                        </SectionErrorBoundary>
                      </ProtectedRoute>
                    } />
                    {/* Isolated visual direction preview — static, no data */}
                    <Route path="/design-preview" element={<DesignPreview />} />
                    {/* Catch-all 404 */}
                    <Route path="*" element={<SectionErrorBoundary section="Not Found"><NotFound /></SectionErrorBoundary>} />
                  </Routes>
                </Suspense>
                  <GlobalCelebrationModal />
                  <PointsCelebrationProvider />
                  <GiftPopupProvider />
                  <RewardReadyPopup />
                  <DailyOfferPopup />
                  <InstallPrompt />
                  <IOSInstallHint />
                  <InstallMomentTrigger />
                  <PwaInstallRewardMount />
                  </AuthProvider>
                </BrowserRouter>
                <Toaster />
                <Sonner />
                <NetworkStatusMonitor />
              </TooltipProvider>
            </CelebrationProvider>
          </LiveRegionProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
